import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeftOnRectangleIcon,
  BanknotesIcon,
  BellIcon,
  ChevronRightIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  DocumentArrowDownIcon,
  DocumentTextIcon,
  HomeIcon,
  PencilSquareIcon,
  PlusIcon,
  ReceiptPercentIcon,
  SparklesIcon,
  TrashIcon,
  UserCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  commerceApi,
  CurrentUser,
  getErrorMessage,
  InterpretationFileItem,
  InterpretationRoleGroup,
  PaymentCreateResult,
  ProjectProgress,
  ProjectSummary,
  projectApi,
  WorkspaceSummary,
  workspaceApi,
  wordExportApi,
  WordExportRecord,
} from '../services/api';
import { draftStorage } from '../utils/draftStorage';
import {
  getProjectStepPath,
  getRecoverableProjectStep,
  parseWorkspaceRoute,
} from '../routing/projectRoutes';
import WorkflowStudio from './WorkflowStudio';
import { saveAs } from 'file-saver';

type WorkspaceView = 'dashboard' | 'workflow' | 'orders' | 'invoice' | 'profile';

interface VisualWorkspaceProps {
  user: CurrentUser;
  onLogout: () => void;
}

interface PromptModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  defaultValue?: string;
  onConfirm: (value: string) => void;
  onCancel: () => void;
}

const PromptModal: React.FC<PromptModalProps> = ({
  isOpen,
  title,
  message,
  defaultValue = '',
  onConfirm,
  onCancel,
}) => {
  const [value, setValue] = useState(defaultValue);

  useEffect(() => {
    if (isOpen) {
      setValue(defaultValue);
    }
  }, [isOpen, defaultValue]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
        <p className="mt-2 text-sm text-slate-500">{message}</p>
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="mt-4 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[var(--bid-color-primary)]"
          autoFocus
        />
        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="rounded-xl border border-slate-200 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50"
          >
            取消
          </button>
          <button
            onClick={() => onConfirm(value)}
            className="rounded-xl bg-[var(--bid-color-primary)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--bid-color-primary-hover)]"
          >
            确定
          </button>
        </div>
      </div>
    </div>
  );
};

const FLOW_META: Record<number, { label: string; color: string; step: number }> = {
  0: { label: '标书设置', color: 'bg-slate-100 text-slate-600', step: 0 },
  10: { label: '招标文件解读', color: 'bg-amber-50 text-amber-700', step: 1 },
  20: { label: '生成目录', color: 'bg-blue-50 text-blue-700', step: 2 },
  25: { label: '已生成编写思路', color: 'bg-indigo-50 text-indigo-700', step: 2 },
  30: { label: '生成正文', color: 'bg-violet-50 text-violet-700', step: 3 },
  40: { label: '已完成', color: 'bg-emerald-50 text-emerald-700', step: 3 },
};

const MILLISECONDS_PER_SECOND = 1000;
const UNIX_MILLISECONDS_THRESHOLD = 100_000_000_000;

const EMPTY_WORKSPACE_SUMMARY: WorkspaceSummary = {
  packages: [],
  payments: [],
  invoices: [],
  usage_records: [],
  messages: [],
  capabilities: {
    payment_enabled: false,
    invoice_application_enabled: false,
    wechat_login_enabled: false,
    payment_reason: '支付能力未配置',
    invoice_reason: '开票能力未配置',
    wechat_login_reason: '微信登录能力未配置',
  },
};

const PAYMENT_STATUS_LABELS: Record<number, string> = {
  0: '待支付',
  10: '已支付',
  20: '支付失败',
  30: '已取消',
  40: '已退款',
};

const INVOICE_STATUS_LABELS: Record<number, string> = {
  0: '待处理',
  5: '开具中',
  10: '已开具',
  [-5]: '已撤销',
  [-10]: '已红冲',
};

export const formatProjectDate = (value: number | string | null) => {
  if (!value) return '暂无更新时间';
  const numericValue = Number(value);
  const normalizedValue = Number.isFinite(numericValue)
    && Math.abs(numericValue) < UNIX_MILLISECONDS_THRESHOLD
    ? numericValue * MILLISECONDS_PER_SECOND
    : value;
  const date = new Date(normalizedValue);
  return Number.isNaN(date.getTime())
    ? String(value)
    : new Intl.DateTimeFormat('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
};

const EmptyState: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
}> = ({ icon, title, description }) => (
  <div className="grid min-h-[420px] place-items-center rounded-2xl border border-dashed border-slate-200 bg-white">
    <div className="max-w-sm text-center">
      <div className="mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-slate-50 text-slate-400">
        {icon}
      </div>
      <h3 className="mt-5 text-base font-semibold text-slate-800">{title}</h3>
      <p className="mt-2 text-sm leading-6 text-slate-500">{description}</p>
    </div>
  </div>
);

const RechargeModal: React.FC<{
  summary: WorkspaceSummary;
  onClose: () => void;
  onPaymentSuccess?: () => void;
}> = ({ summary, onClose, onPaymentSuccess }) => {
  const [selectedPackageId, setSelectedPackageId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [paymentData, setPaymentData] = useState<PaymentCreateResult | null>(null);
  const [polling, setPolling] = useState(false);
  const [payStatus, setPayStatus] = useState<string>('');
  const [error, setError] = useState('');

  // 支付状态轮询定时器
  useEffect(() => {
    if (!paymentData?.id) return;
    let cancelled = false;
    setPolling(true);
    setPayStatus('等待扫码支付...');

    const poll = async () => {
      try {
        const resp = await commerceApi.getPaymentStatus(paymentData.id);
        if (cancelled) return;
        const status = resp.data.data.payment_status;
        // 10 = 已支付
        if (status === 10) {
          setPayStatus('支付成功，正在发放权益...');
          setPolling(false);
          // 给后端一点时间发放权益，然后回调刷新
          setTimeout(() => {
            onPaymentSuccess?.();
            onClose();
          }, 1200);
          return;
        }
        // 30 = 已取消 / 20 = 失败
        if (status === 30 || status === 20) {
          setPayStatus(status === 30 ? '订单已取消' : '支付失败');
          setPolling(false);
          return;
        }
        setPayStatus('等待扫码支付...');
      } catch {
        // 轮询失败忽略，下次重试
      }
    };

    poll();
    const timer = setInterval(poll, 3000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [paymentData?.id, onClose, onPaymentSuccess]);

  const handlePurchase = async (packageId: number) => {
    if (!summary.capabilities.payment_enabled) {
      setError(summary.capabilities.payment_reason || '支付渠道未启用');
      return;
    }
    setSelectedPackageId(packageId);
    setCreating(true);
    setError('');
    setPaymentData(null);
    try {
      // 客户端幂等键：member_id + fee_id + 时间戳
      const idempotencyKey = `recharge-${packageId}-${Date.now()}`;
      const resp = await commerceApi.createPayment({
        fee_id: packageId,
        classify: 'NATIVE',
        idempotency_key: idempotencyKey,
      });
      setPaymentData(resp.data.data);
    } catch (requestError) {
      setError(getErrorMessage(requestError, '创建支付订单失败'));
    } finally {
      setCreating(false);
    }
  };

  const qrImageUrl = paymentData?.gateway?.code_url
    ? `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(paymentData.gateway.code_url)}`
    : '';

  return (
    <div className="fixed inset-0 z-[100] grid place-items-center bg-slate-950/45 p-5 backdrop-blur-sm">
      <div className="w-full max-w-5xl overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-7 py-5">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">计价与支付</h2>
            <p className="mt-1 text-sm text-slate-500">数据来自管理后台的 AI 写标书业务配置</p>
          </div>
          <button onClick={onClose} className="rounded-xl p-2 text-slate-400 hover:bg-slate-100" aria-label="关闭">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        <div className="grid min-h-[470px] gap-6 p-7 lg:grid-cols-[1.25fr_0.75fr]">
          <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 p-6">
            {summary.packages.length > 0 && (
              <div className="space-y-3">
                {summary.packages.map((item) => {
                  const isSelected = selectedPackageId === item.id;
                  const isPaying = isSelected && (creating || polling || Boolean(paymentData));
                  return (
                    <div
                      key={item.id}
                      className={`rounded-xl border bg-white p-5 transition ${
                        isSelected ? 'border-[var(--bid-color-primary)] ring-2 ring-[var(--bid-color-primary)]/20' : 'border-slate-200'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="font-semibold text-slate-800">{item.fees_name}</h3>
                          <p className="mt-2 text-sm text-slate-500">{item.remark || item.fees_type}</p>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-semibold text-[var(--bid-color-primary)]">¥ {item.fees_money}</div>
                          <div className="mt-1 text-xs text-slate-400">
                            {item.add_times > 0 ? `增加 ${item.add_times} 次` : '计价标准'}
                          </div>
                          <button
                            type="button"
                            onClick={() => handlePurchase(item.id)}
                            disabled={isPaying || !summary.capabilities.payment_enabled}
                            className="mt-3 rounded-lg bg-[var(--bid-color-primary)] px-3 py-1.5 text-xs font-medium text-white transition hover:bg-[var(--bid-color-primary-hover)] disabled:cursor-not-allowed disabled:bg-slate-300"
                          >
                            {isPaying ? '支付中...' : '立即购买'}
                          </button>
                        </div>
                      </div>
                      {isSelected && paymentData && (
                        <div className="mt-4 flex items-center gap-4 rounded-lg bg-slate-50 p-3">
                          {qrImageUrl && (
                            <img
                              src={qrImageUrl}
                              alt="微信支付二维码"
                              className="h-32 w-32 rounded-md border border-slate-200 bg-white"
                            />
                          )}
                          <div className="flex-1 text-sm">
                            <div className="font-medium text-slate-800">订单号：{paymentData.payment_no}</div>
                            <div className="mt-1 text-slate-600">应付金额：¥ {paymentData.amount}</div>
                            <div className={`mt-1 ${payStatus.includes('成功') ? 'text-emerald-600' : 'text-slate-500'}`}>
                              {payStatus}
                            </div>
                            <div className="mt-1 text-xs text-slate-400">请使用微信扫码完成支付</div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {summary.packages.length === 0 && (
            <div className="grid h-full place-items-center text-center">
              <div>
                <BanknotesIcon className="mx-auto h-14 w-14 text-slate-300" />
                <h3 className="mt-4 font-semibold text-slate-700">暂无可用套餐数据</h3>
                <p className="mt-2 text-sm leading-6 text-slate-500">
                  套餐接口接入后，此处将按管理后台配置动态展示套餐名称、额度、有效期和价格。
                </p>
              </div>
            </div>
            )}
            {error && (
              <div className="mt-4 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
                {error}
              </div>
            )}
          </div>
          <div className="rounded-2xl border border-slate-200 p-6">
            <div className="text-sm font-medium text-slate-800">支付能力</div>
            <div className={`mt-5 rounded-xl p-4 text-sm leading-6 ${
              summary.capabilities.payment_enabled
                ? 'bg-emerald-50 text-emerald-700'
                : 'bg-amber-50 text-amber-700'
            }`}>
              {summary.capabilities.payment_enabled
                ? '支付渠道已启用，可选择套餐立即购买'
                : summary.capabilities.payment_reason}
            </div>
            <div className="mt-6 text-sm font-medium text-slate-800">最近支付记录</div>
            <div className="mt-3 space-y-2">
              {summary.payments.length === 0 && (
                <p className="rounded-xl bg-slate-50 p-4 text-sm text-slate-500">
                  当前账号暂无 AI 写标书支付记录
                </p>
              )}
              {summary.payments.slice(0, 5).map((item) => (
                <div key={item.id} className="flex items-center justify-between rounded-xl border border-slate-100 px-4 py-3 text-sm">
                  <div>
                    <div className="text-slate-700">{item.remark || item.payment_type}</div>
                    <div className="mt-1 text-xs text-slate-400">{formatProjectDate(item.create_time)}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-medium text-slate-800">¥ {item.amount}</div>
                    <div className="mt-1 text-xs text-slate-500">
                      {PAYMENT_STATUS_LABELS[item.payment_status] || `状态 ${item.payment_status}`}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const MessagePanel: React.FC<{
  summary: WorkspaceSummary;
  onClose: () => void;
  onOpenProject: (projectId: number) => void;
}> = ({ summary, onClose, onOpenProject }) => (
  <div className="fixed inset-0 z-[100] bg-slate-950/20" onClick={onClose}>
    <div
      className="absolute right-8 top-20 w-[420px] rounded-2xl border border-slate-100 bg-white p-5 shadow-2xl"
      onClick={(event) => event.stopPropagation()}
    >
      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-slate-900">项目消息</h2>
        <button onClick={onClose} aria-label="关闭消息" className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100">
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>
      <div className="mt-4 max-h-[480px] space-y-3 overflow-y-auto">
        {summary.messages.length === 0 && (
          <div className="rounded-xl bg-slate-50 p-6 text-center text-sm text-slate-500">
            暂无项目生成异常
          </div>
        )}
        {summary.messages.map((item) => (
          <button
            key={item.id}
            onClick={() => onOpenProject(item.project_id)}
            className="w-full rounded-xl border border-red-100 bg-red-50/50 p-4 text-left hover:bg-red-50"
          >
            <div className="font-medium text-slate-800">{item.title}</div>
            <div className="mt-2 line-clamp-2 text-sm text-red-600">{item.content}</div>
            <div className="mt-2 text-xs text-slate-400">{formatProjectDate(item.create_time)}</div>
          </button>
        ))}
      </div>
    </div>
  </div>
);

const INTERPRETATION_TABS: { key: InterpretationRoleGroup; label: string }[] = [
  { key: 'tender', label: '招标文件解读列表' },
  { key: 'list', label: '项目清单解读列表' },
  { key: 'drawing', label: '工程图纸解读列表' },
];

const Dashboard: React.FC<{
  projects: ProjectSummary[];
  progressByProject: Record<number, ProjectProgress>;
  isLoading: boolean;
  error: string;
  onCreate: () => void;
  onOpen: (project: ProjectSummary) => void;
  onRename: (project: ProjectSummary) => void;
  onDelete: (project: ProjectSummary) => void;
  onDownload: (project: ProjectSummary) => void;
  onRefresh: () => void;
  // 解读列表相关props
  interpretationTab: InterpretationRoleGroup;
  setInterpretationTab: (tab: InterpretationRoleGroup) => void;
  interpretationList: InterpretationFileItem[];
  interpretationTotal: number;
  interpretationLoading: boolean;
  onViewInterpretation: (file: InterpretationFileItem) => void;
}> = ({
  projects,
  progressByProject,
  isLoading,
  error,
  onCreate,
  onOpen,
  onRename,
  onDelete,
  onDownload,
  onRefresh,
  interpretationTab,
  setInterpretationTab,
  interpretationList,
  interpretationTotal,
  interpretationLoading,
  onViewInterpretation,
}) => {
  const completed = projects.filter((project) => project.flow_status === 40).length;
  const processing = projects.filter(
    (project) => project.flow_status > 0 && project.flow_status < 40
  ).length;

  return (
    <div className="mx-auto w-full max-w-[1380px]">
      <div className="flex flex-col justify-between gap-5 sm:flex-row sm:items-end">
        <div>
          <p className="text-sm font-medium text-[var(--bid-color-primary)]">工作台</p>
          <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-900">
            欢迎回来，开始今天的标书工作
          </h1>
          <p className="mt-2 text-sm text-slate-500">管理项目并继续上次未完成的编制任务</p>
        </div>
        <button
          onClick={onCreate}
          className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-[var(--bid-color-primary)] px-5 text-sm font-medium text-white shadow-lg shadow-violet-200 hover:bg-[var(--bid-color-primary-hover)]"
        >
          <PlusIcon className="h-5 w-5" />
          新建标书
        </button>
      </div>

      <div className="mt-8 grid gap-5 md:grid-cols-3">
        {isLoading ? (
          [1, 2, 3].map((item) => (
            <div key={item} className="rounded-2xl border border-slate-100 bg-white p-6 shadow-[0_8px_30px_rgba(31,52,86,0.05)]">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="h-4 w-16 animate-pulse rounded bg-slate-100" />
                  <div className="mt-2 h-9 w-12 animate-pulse rounded bg-slate-100" />
                </div>
                <div className="h-12 w-12 animate-pulse rounded-2xl bg-slate-100" />
              </div>
            </div>
          ))
        ) : (
          [
            { label: '全部项目', value: projects.length, icon: ClipboardDocumentListIcon, iconClass: 'bg-blue-50 text-blue-600' },
            { label: '进行中', value: processing, icon: ClockIcon, iconClass: 'bg-amber-50 text-amber-600' },
            { label: '已完成', value: completed, icon: DocumentTextIcon, iconClass: 'bg-emerald-50 text-emerald-600' },
          ].map((item) => (
            <div key={item.label} className="rounded-2xl border border-slate-100 bg-white p-6 shadow-[0_8px_30px_rgba(31,52,86,0.05)]">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-slate-500">{item.label}</div>
                  <div className="mt-2 text-3xl font-semibold text-slate-900">{item.value}</div>
                </div>
                <div className={`grid h-12 w-12 place-items-center rounded-2xl ${item.iconClass}`}>
                  <item.icon className="h-6 w-6" />
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="mt-7 overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-[0_8px_30px_rgba(31,52,86,0.05)]">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-5">
          <div>
            <h2 className="font-semibold text-slate-900">标书任务</h2>
            <p className="mt-1 text-xs text-slate-500">按资料、解析、目录和实际生成章节动态计算</p>
          </div>
          <button onClick={onRefresh} className="text-sm font-medium text-[var(--bid-color-primary)] hover:text-[var(--bid-color-primary-hover)]">
            刷新
          </button>
        </div>

        {isLoading ? (
          <div className="space-y-4 p-6">
            {[1, 2, 3].map((item) => (
              <div key={item} className="h-20 animate-pulse rounded-xl bg-slate-100" />
            ))}
          </div>
        ) : error ? (
          <div className="p-10 text-center">
            <p className="text-sm text-red-600">{error}</p>
            <button onClick={onRefresh} className="mt-4 text-sm font-medium text-[var(--bid-color-primary)]">重新加载</button>
          </div>
        ) : projects.length === 0 ? (
          <div className="grid min-h-[300px] place-items-center p-8 text-center">
            <div>
              <div className="mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-blue-50 text-blue-500">
                <DocumentTextIcon className="h-8 w-8" />
              </div>
              <h3 className="mt-5 font-semibold text-slate-800">还没有标书项目</h3>
              <p className="mt-2 text-sm text-slate-500">创建第一个项目，开始四步标书编制流程</p>
              <button onClick={onCreate} className="mt-5 rounded-xl bg-[var(--bid-color-primary)] px-5 py-2.5 text-sm font-medium text-white">
                新建标书
              </button>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {projects.map((project) => {
              const meta = FLOW_META[project.flow_status] || FLOW_META[0];
              const progressInfo = progressByProject[project.id];
              const progress = progressInfo?.progress_percent ?? 0;
              return (
                <div
                  key={project.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => onOpen(project)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') onOpen(project);
                  }}
                  className="grid w-full cursor-pointer grid-cols-[minmax(0,1fr)_140px_220px_112px] items-center gap-5 px-6 py-5 text-left transition hover:bg-slate-50/80"
                >
                  <div className="flex min-w-0 items-center gap-4">
                    <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[var(--bid-color-primary-soft)] text-[var(--bid-color-primary)]">
                      <DocumentTextIcon className="h-6 w-6" />
                    </div>
                    <div className="min-w-0">
                      <div className="truncate font-medium text-slate-800">{project.project_name}</div>
                      <div className="mt-1 text-xs text-slate-400">更新于 {formatProjectDate(project.update_time)}</div>
                    </div>
                  </div>
                  <span className={`w-fit rounded-full px-3 py-1 text-xs font-medium ${meta.color}`}>
                    {meta.label}
                  </span>
                  <div>
                    <div className="mb-2 flex justify-between text-xs text-slate-500">
                      <span>
                        {progressInfo?.outline_leaf_total
                          ? `${progressInfo.generated_leaf_count}/${progressInfo.outline_leaf_total} 章节`
                          : '编制进度'}
                      </span>
                      <span>{progress}%</span>
                    </div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
                      <div className="h-full rounded-full bg-[var(--bid-color-primary)]" style={{ width: `${progress}%` }} />
                    </div>
                    {Boolean(progressInfo?.actual_word_count) && (
                      <div className="mt-1 text-right text-[11px] text-slate-400">
                        已生成 {progressInfo.actual_word_count.toLocaleString()} 字
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-end gap-1">
                    <button
                      type="button"
                      title="重命名"
                      aria-label={`重命名 ${project.project_name}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        onRename(project);
                      }}
                      className="rounded-lg p-2 text-slate-400 hover:bg-blue-50 hover:text-blue-600"
                    >
                      <PencilSquareIcon className="h-4 w-4" />
                    </button>
                    {project.flow_status >= 30
                      && (progressByProject[project.id]?.generated_leaf_count ?? 0) > 0 && (
                      <button
                        type="button"
                        title="下载"
                        aria-label={`下载 ${project.project_name}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          onDownload(project);
                        }}
                        className="rounded-lg p-2 text-slate-400 hover:bg-emerald-50 hover:text-emerald-600"
                      >
                        <DocumentArrowDownIcon className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      type="button"
                      title="删除"
                      aria-label={`删除 ${project.project_name}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        onDelete(project);
                      }}
                      className="rounded-lg p-2 text-slate-400 hover:bg-red-50 hover:text-red-600"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                    <ChevronRightIcon className="ml-1 h-5 w-5 text-slate-300" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 解读文件列表区域 */}
      <div className="mt-7 overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-[0_8px_30px_rgba(31,52,86,0.05)]">
        <div className="border-b border-slate-100 px-6 py-5">
          <h2 className="font-semibold text-slate-900">解读文件</h2>
          <p className="mt-1 text-xs text-slate-500">查看和管理所有解读文件</p>
          
          {/* Tab切换 */}
          <div className="mt-4 flex gap-2 border-b border-slate-100">
            {INTERPRETATION_TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setInterpretationTab(tab.key)}
                className={`px-4 py-2 text-sm font-medium transition ${
                  interpretationTab === tab.key
                    ? 'border-b-2 border-[var(--bid-color-primary)] text-[var(--bid-color-primary)]'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* 列表内容 */}
        {interpretationLoading ? (
          <div className="space-y-4 p-6">
            {[1, 2, 3].map((item) => (
              <div key={item} className="h-12 animate-pulse rounded-xl bg-slate-100" />
            ))}
          </div>
        ) : interpretationList.length === 0 ? (
          <div className="grid min-h-[200px] place-items-center p-8 text-center">
            <div>
              <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-blue-50 text-blue-500">
                <DocumentTextIcon className="h-6 w-6" />
              </div>
              <p className="mt-4 text-sm text-slate-500">暂无{INTERPRETATION_TABS.find(t => t.key === interpretationTab)?.label}</p>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {/* 表头 */}
            <div className="grid grid-cols-[60px_1fr_1fr_150px_100px] gap-4 bg-slate-50 px-6 py-3 text-xs font-medium text-slate-500">
              <span>序号</span>
              <span>标书名</span>
              <span>解读文件名</span>
              <span>创建时间</span>
              <span className="text-center">操作</span>
            </div>
            {/* 列表项 */}
            {interpretationList.map((item, idx) => (
              <div
                key={item.id}
                className="grid grid-cols-[60px_1fr_1fr_150px_100px] items-center gap-4 px-6 py-4 text-sm transition hover:bg-slate-50"
              >
                <span className="text-slate-400">{idx + 1}</span>
                <span className="truncate font-medium text-slate-800">{item.project_name}</span>
                <span className="truncate text-slate-600">{item.file_name}</span>
                <span className="text-slate-500">{item.create_time_str}</span>
                <span className="text-center">
                  <button
                    onClick={() => onViewInterpretation(item)}
                    className="text-[var(--bid-color-primary)] hover:underline"
                  >
                    查看
                  </button>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const VisualWorkspace: React.FC<VisualWorkspaceProps> = ({ user, onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const workspaceRoute = useMemo(
    () => parseWorkspaceRoute(location.pathname),
    [location.pathname]
  );
  const initialView: WorkspaceView = workspaceRoute.section === 'projects'
    ? 'dashboard'
    : workspaceRoute.section === 'invoices'
      ? 'invoice'
      : workspaceRoute.section;
  const [view, setView] = useState<WorkspaceView>(initialView);
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [progressByProject, setProgressByProject] = useState<Record<number, ProjectProgress>>({});
  const [selectedProject, setSelectedProject] = useState<ProjectSummary | null>(null);

  useEffect(() => {
    const newView: WorkspaceView = workspaceRoute.section === 'projects'
      ? 'dashboard'
      : workspaceRoute.section === 'invoices'
        ? 'invoice'
        : workspaceRoute.section;
    setView(newView);
  }, [workspaceRoute.section]);
  const [workspaceSummary, setWorkspaceSummary] = useState<WorkspaceSummary>(
    EMPTY_WORKSPACE_SUMMARY
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [workspaceError, setWorkspaceError] = useState('');
  const [showRecharge, setShowRecharge] = useState(false);
  const [showMessages, setShowMessages] = useState(false);
  
  // 解读列表相关状态
  const [interpretationTab, setInterpretationTab] = useState<InterpretationRoleGroup>('tender');
  const [interpretationList, setInterpretationList] = useState<InterpretationFileItem[]>([]);
  const [interpretationTotal, setInterpretationTotal] = useState(0);
  const [interpretationLoading, setInterpretationLoading] = useState(false);
  
  const loadInterpretationList = useCallback(async (roleGroup: InterpretationRoleGroup) => {
    setInterpretationLoading(true);
    try {
      const response = await workspaceApi.interpretationList(roleGroup);
      const data = response.data.data;
      setInterpretationList(data.list || []);
      setInterpretationTotal(data.total || 0);
    } catch (requestError) {
      console.error('解读列表加载失败:', requestError);
      setInterpretationList([]);
      setInterpretationTotal(0);
    } finally {
      setInterpretationLoading(false);
    }
  }, []);
  
  useEffect(() => {
    if (view === 'dashboard') {
      loadInterpretationList(interpretationTab);
    }
  }, [view, interpretationTab, loadInterpretationList]);

  const [promptState, setPromptState] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    defaultValue: string;
    onConfirm: (value: string) => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    defaultValue: '',
    onConfirm: () => {},
  });

  const openPrompt = (
    title: string,
    message: string,
    defaultValue: string,
    onConfirm: (value: string) => void
  ) => {
    setPromptState({ isOpen: true, title, message, defaultValue, onConfirm });
  };

  const closePrompt = () => {
    setPromptState((prev) => ({ ...prev, isOpen: false }));
  };

  const loadWorkspaceSummary = useCallback(async () => {
    setWorkspaceError('');
    try {
      const response = await workspaceApi.summary();
      setWorkspaceSummary(response.data.data);
    } catch (requestError) {
      setWorkspaceError(getErrorMessage(requestError, '账户数据加载失败'));
    }
  }, []);

  /**
   * 申请退款：用 window.prompt 收集退款金额和原因，调用 commerceApi.createRefund。
   * 退款申请提交后由 pear 管理后台审核，本接口仅创建退款记录。
   */
  const handleRefund = useCallback(async (paymentId: number, paymentAmount: string) => {
    const refundMoney = window.prompt(
      `请输入退款金额（元，最多 ${paymentAmount} 元）`,
      paymentAmount
    );
    if (refundMoney === null) return;
    if (!refundMoney || Number(refundMoney) <= 0) {
      window.alert('退款金额必须大于 0');
      return;
    }
    if (Number(refundMoney) > Number(paymentAmount)) {
      window.alert(`退款金额不能超过 ${paymentAmount} 元`);
      return;
    }
    const refundReason = window.prompt('请输入退款原因（选填）', '') || '';
    try {
      await commerceApi.createRefund({
        payment_id: paymentId,
        refund_money: refundMoney,
        refund_reason: refundReason,
      });
      window.alert('退款申请已提交，等待管理后台审核');
      // 刷新订单和发票数据
      loadWorkspaceSummary();
    } catch (requestError) {
      window.alert(getErrorMessage(requestError, '退款申请失败'));
    }
  }, [loadWorkspaceSummary]);

  /**
   * 申请发票：
   * 1. 调用 commerceApi.listInvoiceEligiblePayments 拿可开票支付记录；
   * 2. 用 window.prompt 让用户输入 payment_id 和开票信息；
   * 3. 调用 commerceApi.createInvoice 提交申请。
   */
  const handleCreateInvoice = useCallback(async () => {
    if (!workspaceSummary.capabilities.invoice_application_enabled) {
      window.alert(workspaceSummary.capabilities.invoice_reason || '开票功能未启用');
      return;
    }
    try {
      const resp = await commerceApi.listInvoiceEligiblePayments();
      const { items, application_enabled, application_reason } = resp.data.data;
      if (!application_enabled) {
        window.alert(application_reason || '开票功能未启用');
        return;
      }
      if (!items.length) {
        window.alert('当前没有可开票的支付记录（已支付订单才能开票）');
        return;
      }
      const listText = items
        .map((it) => `ID=${it.id} 金额=¥${it.amount} 套餐=${it.fee_name} 时间=${formatProjectDate(it.payment_time)}${it.already_invoiced ? '（已开票）' : ''}`)
        .join('\n');
      const paymentIdInput = window.prompt(
        `请选择要开票的支付记录ID：\n\n${listText}\n\n请输入 ID：`
      );
      if (!paymentIdInput) return;
      const paymentId = Number(paymentIdInput);
      const selected = items.find((it) => it.id === paymentId);
      if (!selected) {
        window.alert('未找到对应的支付记录');
        return;
      }
      if (selected.already_invoiced) {
        window.alert('该支付记录已开票');
        return;
      }
      const companyName = window.prompt('请输入发票抬头（公司名称）', '');
      if (!companyName) return;
      const taxation = window.prompt('请输入纳税人识别号', '');
      if (!taxation) return;
      const remark = window.prompt('请输入发票备注（选填）', '') || '';
      await commerceApi.createInvoice({
        payment_id: paymentId,
        company_name: companyName,
        taxation,
        remark,
      });
      window.alert('发票申请已提交，等待第三方开票服务处理');
      loadWorkspaceSummary();
    } catch (requestError) {
      window.alert(getErrorMessage(requestError, '申请发票失败'));
    }
  }, [workspaceSummary.capabilities, loadWorkspaceSummary]);

  const loadProjects = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const [projectResponse, progressResponse] = await Promise.all([
        projectApi.list(),
        projectApi.progressList(),
      ]);

      const projectsData = projectResponse?.data?.data?.list;
      if (Array.isArray(projectsData)) {
        setProjects(projectsData);
      } else {
        setProjects([]);
      }

      const progressData = progressResponse?.data?.data?.list;
      if (Array.isArray(progressData)) {
        setProgressByProject(
          Object.fromEntries(
            progressData.map((item) => [item.project_id, item])
          )
        );
      }
    } catch (requestError) {
      console.error('项目列表加载失败:', requestError);
      setError(getErrorMessage(requestError, '项目列表加载失败'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProjects();
    loadWorkspaceSummary();
  }, [loadProjects, loadWorkspaceSummary]);

  useEffect(() => {
    const nextView: WorkspaceView = workspaceRoute.section === 'projects'
      ? 'dashboard'
      : workspaceRoute.section === 'invoices'
        ? 'invoice'
        : workspaceRoute.section;
    setView(nextView);

    if (workspaceRoute.projectId) {
      // 优先匹配 projects 列表；匹配不到但 selectedProject.id 恰好等于 route 指向时也放行
      // （避免刚创建完项目、列表还未重新加载时被错误踢回工作台）
      const routeProject = projects.find(
        (item) => item.id === workspaceRoute.projectId
      );
      if (routeProject) {
        setSelectedProject(routeProject);
        draftStorage.setProjectId(routeProject.id);
      } else if (
        selectedProject?.id === workspaceRoute.projectId &&
        workspaceRoute.step >= 0
      ) {
        // 刚创建项目时在 onProjectCreated 中已写入 selectedProject，
        // 此时直接沿用，不做跳转拦截，不展示错误
      } else if (!isLoading) {
        setError('项目不存在或当前账号无权访问');
        navigate('/projects', { replace: true });
      }
    } else if (!workspaceRoute.isNewProject) {
      setSelectedProject(null);
    }
  }, [isLoading, navigate, projects, selectedProject, workspaceRoute]);

  const currentTitle = useMemo(() => {
    if (view === 'orders') return '我的订单';
    if (view === 'invoice') return '开票记录';
    if (view === 'profile') return '个人中心';
    if (view === 'workflow') return selectedProject?.project_name || '新建标书';
    return '标书工作台';
  }, [selectedProject, view]);

  const openProject = (project: ProjectSummary | null) => {
    draftStorage.setProjectId(project?.id ?? null);
    setSelectedProject(project);
    setView('workflow');
    navigate(
      project
        ? getProjectStepPath(project.id, getRecoverableProjectStep(project))
        : '/projects/new'
    );
  };

  const openProjectById = (projectId: number) => {
    const project = projects.find((item) => item.id === projectId);
    setShowMessages(false);
    if (project) {
      openProject(project);
      return;
    }
    setError('消息对应的项目当前不可用，请刷新项目列表后重试');
    setView('dashboard');
    navigate('/projects');
  };

  const finishWorkflow = () => {
    setView('dashboard');
    setSelectedProject(null);
    navigate('/projects');
    loadProjects();
  };

  const renameProject = (project: ProjectSummary) => {
    openPrompt(
      '重命名项目',
      '请输入新的项目名称',
      project.project_name,
      async (projectName) => {
        const trimmedName = projectName.trim();
        if (!trimmedName || trimmedName === project.project_name) {
          closePrompt();
          return;
        }
        try {
          await projectApi.update(project.id, { project_name: trimmedName });
          await loadProjects();
          closePrompt();
        } catch (requestError) {
          setError(getErrorMessage(requestError, '项目重命名失败'));
          closePrompt();
        }
      }
    );
  };

  const deleteProject = async (project: ProjectSummary) => {
    if (!window.confirm(`确定删除“${project.project_name}”吗？项目将进入软删除状态。`)) {
      return;
    }
    try {
      await projectApi.delete(project.id);
      await loadProjects();
    } catch (requestError) {
      setError(getErrorMessage(requestError, '项目删除失败'));
    }
  };

  const downloadProject = async (project: ProjectSummary) => {
    try {
      setError('');
      const createResponse = await wordExportApi.create(project.id, {
        export_type: 'docx',
      });
      const exportRecord: WordExportRecord = createResponse.data.data.export;

      if (!exportRecord.downloadable) {
        let current: WordExportRecord = exportRecord;
        for (let i = 0; i < 60; i++) {
          await new Promise((r) => setTimeout(r, 1000));
          const pollResponse = await wordExportApi.detail(project.id, exportRecord.id);
          current = pollResponse.data.data.export;
          if (current.downloadable) break;
          if (current.status_name === 'FAILED'
              || current.status_name === 'CANCELLED'
              || current.status_name === 'EXPIRED') {
            const task = pollResponse.data.data.task;
            const errMsg =
              (task && (task as any).error_message) || current.error_message || '导出失败';
            setError(errMsg);
            return;
          }
        }
        if (!current.downloadable) {
          setError('导出超时，请稍后重试或查看导出记录');
          return;
        }
        exportRecord.id = current.id;
      }

      const blobResponse = await wordExportApi.download(project.id, exportRecord.id);
      saveAs(
        blobResponse.data,
        exportRecord.file_name || `${project.project_name}.docx`
      );
    } catch (requestError) {
      setError(getErrorMessage(requestError, '项目下载失败'));
    }
  };

  const navigateToView = (target: WorkspaceView) => {
    setSelectedProject(null);
    setView(target);
    const paths: Record<Exclude<WorkspaceView, 'workflow'>, string> = {
      dashboard: '/projects',
      orders: '/orders',
      invoice: '/invoices',
      profile: '/profile/bind-wechat',
    };
    if (target !== 'workflow') {
      navigate(paths[target]);
    }
  };

  const navItems = [
    { id: 'dashboard' as const, label: '我的标书', icon: HomeIcon },
    { id: 'orders' as const, label: '我的订单', icon: ClipboardDocumentListIcon },
    { id: 'invoice' as const, label: '开票记录', icon: ReceiptPercentIcon },
    { id: 'profile' as const, label: '个人中心', icon: UserCircleIcon },
  ];

  return (
    <div className="flex h-screen min-w-[1180px] overflow-hidden bg-[#f5f7fb] text-slate-800">
      <aside className="flex w-[232px] shrink-0 flex-col bg-[#17223c] text-white">
        <div className="flex h-[76px] items-center gap-3 border-b border-white/10 px-6">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-[var(--bid-color-primary-strong)] shadow-lg shadow-violet-950/30">
            <SparklesIcon className="h-6 w-6" />
          </div>
          <div>
            <div className="text-lg font-semibold tracking-wide">易成标</div>
            <div className="text-[11px] text-slate-400">AI 标书编制平台</div>
          </div>
        </div>

        <nav className="flex-1 space-y-2 px-3 py-6">
          <div className="px-3 pb-2 text-[11px] font-medium tracking-[0.18em] text-slate-500">工作空间</div>
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => {
                navigateToView(item.id);
              }}
              className={`flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm transition ${
                view === item.id || (item.id === 'dashboard' && view === 'workflow')
                  ? 'bg-[var(--bid-color-primary)] text-white shadow-lg shadow-violet-950/20'
                  : 'text-slate-300 hover:bg-white/5 hover:text-white'
              }`}
            >
              <item.icon className="h-5 w-5" />
              {item.label}
            </button>
          ))}
        </nav>

        <div className="border-t border-white/10 p-4">
          <button
            onClick={onLogout}
            className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm text-slate-400 hover:bg-white/5 hover:text-white"
          >
            <ArrowLeftOnRectangleIcon className="h-5 w-5" />
            退出登录
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-[76px] shrink-0 items-center justify-between border-b border-slate-200/80 bg-white px-8">
          <div>
            <div className="text-xs text-slate-400">易成标 / {currentTitle}</div>
            <div className="mt-1 font-medium text-slate-800">{currentTitle}</div>
          </div>
          <div className="flex items-center gap-5">
            <button onClick={() => navigateToView('invoice')} className="text-sm text-slate-600 hover:text-[var(--bid-color-primary)]">开发票</button>
            <div className="h-5 w-px bg-slate-200" />
            <div className="text-sm text-slate-500">
              剩余字数 <span className="ml-1 font-semibold text-slate-900">{user.available_words}</span>
            </div>
            <button
              onClick={() => setShowRecharge(true)}
              className="rounded-xl bg-[#fff2ea] px-4 py-2 text-sm font-medium text-[#f06b32] hover:bg-[#ffe7da]"
            >
              立即充值
            </button>
            <button
              onClick={() => setShowMessages(true)}
              className="relative rounded-xl p-2 text-slate-400 hover:bg-slate-100"
              aria-label="消息"
            >
              <BellIcon className="h-5 w-5" />
              {workspaceSummary.messages.length > 0 && (
                <span className="absolute -right-1 -top-1 min-w-5 rounded-full bg-red-500 px-1.5 py-0.5 text-center text-[10px] font-semibold leading-4 text-white">
                  {workspaceSummary.messages.length > 99
                    ? '99+'
                    : workspaceSummary.messages.length}
                </span>
              )}
            </button>
            <button onClick={() => navigateToView('profile')} className="flex items-center gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-full bg-gradient-to-br from-[#8f74ff] to-[var(--bid-color-primary)] text-sm font-semibold text-white">
                {user.username.slice(0, 1).toUpperCase()}
              </div>
              <div className="text-left">
                <div className="text-sm font-medium text-slate-800">{user.username}</div>
                <div className="text-xs text-slate-400">普通用户</div>
              </div>
            </button>
          </div>
        </header>

        <main
          id="visual-workspace-scroll"
          className="min-h-0 flex-1 overflow-y-auto p-8"
        >
          {view === 'dashboard' && (
            <Dashboard
              projects={projects}
              progressByProject={progressByProject}
              isLoading={isLoading}
              error={error}
              onCreate={() => openProject(null)}
              onOpen={openProject}
              onRename={renameProject}
              onDelete={deleteProject}
              onDownload={downloadProject}
              onRefresh={loadProjects}
              interpretationTab={interpretationTab}
              setInterpretationTab={setInterpretationTab}
              interpretationList={interpretationList}
              interpretationTotal={interpretationTotal}
              interpretationLoading={interpretationLoading}
              onViewInterpretation={(file) => {
                // 跳转到对应项目查看
                const project = projects.find(p => p.id === file.project_id);
                if (project) {
                  openProject(project);
                }
              }}
            />
          )}
          {view === 'workflow' && (
            <WorkflowStudio
              project={selectedProject}
              routeStep={workspaceRoute.step}
              onBack={finishWorkflow}
              onProjectCreated={(project) => {
                setProjects((previous) => [
                  project,
                  ...previous.filter((item) => item.id !== project.id),
                ]);
                setSelectedProject(project);
                draftStorage.setProjectId(project.id);
                // 立即将 URL 切换到该项目的第 1 步（避免 /projects/new 在 useEffect 中被误判）
                navigate(getProjectStepPath(project.id, 1), { replace: true });
              }}
              onRouteChange={(step, activeProjectId) => {
                const nextProjectId = activeProjectId || selectedProject?.id;
                if (nextProjectId) {
                  navigate(getProjectStepPath(nextProjectId, step));
                }
              }}
            />
          )}
          {view === 'orders' && (
            <div className="mx-auto max-w-[1380px]">
              <div className="flex items-end justify-between">
                <div>
                  <h1 className="text-2xl font-semibold text-slate-900">我的订单</h1>
                  <p className="mt-2 text-sm text-slate-500">
                    查看套餐订单、支付状态和开票进度
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowRecharge(true)}
                  className="bid-primary-button"
                >
                  购买套餐
                </button>
              </div>
              <div className="mt-7">
                {workspaceSummary.payments.length === 0 ? (
                  <EmptyState
                    icon={<ClipboardDocumentListIcon className="h-8 w-8" />}
                    title="暂无订单"
                    description={workspaceError || '购买套餐后，订单会显示在这里。'}
                  />
                ) : (
                  <div className="overflow-hidden rounded-2xl border border-[var(--bid-color-primary-soft)] bg-white shadow-[0_8px_30px_rgba(53,57,73,0.05)]">
                    <div className="grid grid-cols-[110px_minmax(180px,1fr)_140px_130px_170px_160px] gap-4 border-b border-slate-100 bg-slate-50 px-6 py-4 text-xs font-medium text-slate-500">
                      <span>订单编号</span>
                      <span>套餐类型</span>
                      <span>实付金额</span>
                      <span>支付状态</span>
                      <span>开通时间</span>
                      <span>操作</span>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {workspaceSummary.payments.map((item) => (
                        <div
                          key={item.id}
                          className="grid grid-cols-[110px_minmax(180px,1fr)_140px_130px_170px_160px] items-center gap-4 px-6 py-5 text-sm"
                        >
                          <span className="text-slate-500">#{item.id}</span>
                          <span className="truncate font-medium text-slate-800">
                            {item.payment_type || '套餐订单'}
                          </span>
                          <span className="text-slate-700">¥ {item.amount}</span>
                          <span className="text-slate-600">
                            {PAYMENT_STATUS_LABELS[item.payment_status]
                              || `状态 ${item.payment_status}`}
                          </span>
                          <span className="text-slate-500">
                            {formatProjectDate(item.payment_time || item.create_time)}
                          </span>
                          <div className="flex flex-wrap items-center gap-2">
                            <button
                              type="button"
                              onClick={() => navigateToView('invoice')}
                              className="text-left text-[var(--bid-color-primary)] hover:underline"
                            >
                              查看发票
                            </button>
                            {item.payment_status === 10 && (
                              <button
                                type="button"
                                onClick={() => handleRefund(item.id, item.amount)}
                                className="text-left text-amber-600 hover:underline"
                              >
                                申请退款
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {!workspaceSummary.capabilities.payment_enabled && (
                <p className="mt-4 text-sm text-amber-700">
                  {workspaceSummary.capabilities.payment_reason}
                </p>
              )}
            </div>
          )}
          {view === 'invoice' && (
            <div className="mx-auto max-w-[1380px]">
              <div className="flex items-end justify-between">
                <div>
                  <h1 className="text-2xl font-semibold text-slate-900">开票记录</h1>
                  <p className="mt-2 text-sm text-slate-500">查看当前账号的发票申请和开具状态</p>
                </div>
                <button
                  type="button"
                  onClick={handleCreateInvoice}
                  disabled={!workspaceSummary.capabilities.invoice_application_enabled}
                  className="bid-primary-button disabled:cursor-not-allowed disabled:bg-slate-300"
                >
                  申请发票
                </button>
              </div>
              <div className="mt-7">
                {workspaceSummary.invoices.length === 0 ? (
                <EmptyState
                  icon={<ReceiptPercentIcon className="h-8 w-8" />}
                  title="暂无开票记录"
                  description={workspaceError || workspaceSummary.capabilities.invoice_reason}
                />
                ) : (
                  <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-[0_8px_30px_rgba(31,52,86,0.05)]">
                    <div className="grid grid-cols-[120px_minmax(200px,1fr)_140px_120px_170px] gap-4 border-b border-slate-100 bg-slate-50 px-6 py-4 text-xs font-medium text-slate-500">
                      <span>发票编号</span>
                      <span>开票单位</span>
                      <span>金额</span>
                      <span>状态</span>
                      <span>申请时间</span>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {workspaceSummary.invoices.map((item) => (
                        <div
                          key={item.id}
                          className="grid grid-cols-[120px_minmax(200px,1fr)_140px_120px_170px] items-center gap-4 px-6 py-5 text-sm"
                        >
                          <span className="text-slate-500">#{item.id}</span>
                          <span className="truncate font-medium text-slate-800">{item.company_name}</span>
                          <span className="text-slate-700">¥ {item.money}</span>
                          <span className="text-slate-600">
                            {INVOICE_STATUS_LABELS[item.status] || `状态 ${item.status}`}
                          </span>
                          <span className="text-slate-500">{formatProjectDate(item.create_time)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <p className="mt-4 text-sm text-amber-700">
                {workspaceSummary.capabilities.invoice_reason}
              </p>
            </div>
          )}
          {view === 'profile' && (
            <div className="mx-auto max-w-[1380px]">
              <div className="rounded-2xl bg-gradient-to-r from-[var(--bid-color-primary)] to-[#8f74ff] p-8 text-white shadow-lg shadow-violet-100">
                <div className="flex items-center gap-5">
                  <div className="grid h-20 w-20 place-items-center rounded-full bg-white/20 text-3xl font-semibold ring-4 ring-white/10">
                    {user.username.slice(0, 1).toUpperCase()}
                  </div>
                  <div>
                    <h1 className="text-2xl font-semibold">{user.username}</h1>
                    <p className="mt-2 text-sm text-blue-100">用户编号：{user.id}</p>
                  </div>
                </div>
              </div>
              <div className="mt-6 grid gap-5 md:grid-cols-2">
                <div className="rounded-2xl border border-slate-100 bg-white p-6">
                  <div className="text-sm text-slate-500">剩余字数</div>
                  <div className="mt-2 text-2xl font-semibold text-slate-900">{user.available_words}</div>
                </div>
                <div className="rounded-2xl border border-slate-100 bg-white p-6">
                  <div className="text-sm text-slate-500">账户余额</div>
                  <div className="mt-2 text-2xl font-semibold text-slate-900">¥ {user.balance}</div>
                </div>
              </div>
              <div className="mt-6">
                {workspaceSummary.usage_records.length === 0 ? (
                <EmptyState
                  icon={<ClockIcon className="h-8 w-8" />}
                  title="暂无使用记录"
                  description={workspaceError || '当前账号暂无额度变动或 AI 操作记录。'}
                />
                ) : (
                  <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-[0_8px_30px_rgba(31,52,86,0.05)]">
                    <div className="grid grid-cols-[150px_minmax(180px,1fr)_150px_150px_180px] gap-4 border-b border-slate-100 bg-slate-50 px-6 py-4 text-xs font-medium text-slate-500">
                      <span>记录类型</span>
                      <span>操作</span>
                      <span>变动字数</span>
                      <span>可用字数</span>
                      <span>时间</span>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {workspaceSummary.usage_records.map((item) => (
                        <div
                          key={item.id}
                          className="grid grid-cols-[150px_minmax(180px,1fr)_150px_150px_180px] items-center gap-4 px-6 py-5 text-sm"
                        >
                          <span className="text-slate-600">
                            {item.record_type === 'quota' ? '额度变动' : 'AI 操作'}
                          </span>
                          <div className="min-w-0">
                            <div className="truncate font-medium text-slate-800">{item.operation}</div>
                            {item.remark && (
                              <div className="mt-1 truncate text-xs text-slate-400">{item.remark}</div>
                            )}
                          </div>
                          <span className="text-slate-700">
                            {item.amount === null
                              ? '-'
                              : `${item.amount > 0 ? '+' : ''}${item.amount.toLocaleString()} 字`}
                          </span>
                          <span className="text-slate-700">
                            {item.available_after === null
                              ? '-'
                              : `${item.available_after.toLocaleString()} 字`}
                          </span>
                          <span className="text-slate-500">{formatProjectDate(item.create_time)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </main>
      </div>
      {showRecharge && (
        <RechargeModal
          summary={workspaceSummary}
          onClose={() => setShowRecharge(false)}
          onPaymentSuccess={loadWorkspaceSummary}
        />
      )}
      {showMessages && (
        <MessagePanel
          summary={workspaceSummary}
          onClose={() => setShowMessages(false)}
          onOpenProject={openProjectById}
        />
      )}
      <PromptModal
        isOpen={promptState.isOpen}
        title={promptState.title}
        message={promptState.message}
        defaultValue={promptState.defaultValue}
        onConfirm={promptState.onConfirm}
        onCancel={closePrompt}
      />
    </div>
  );
};

export default VisualWorkspace;